import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useI18n } from '../../i18n/useI18n';
import { Theme, themeService, ThemeServiceContext } from '../../services/themeService';
import { ThemeChannel } from '../../utils/themePaths';

type AvailabilityDraft = {
  liteCaptureEnabled: boolean;
  mobileUploadEnabled: boolean;
  scheduleEnabled: boolean;
  availableFrom: string;
  availableUntil: string;
  machineFilterEnabled: boolean;
  allowedMachineCodes: string[];
};

function pad(value: number) {
  return String(value).padStart(2, '0');
}

export function toLocalInput(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatAvailabilityStamp(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function isInsideSchedule(theme: Pick<Theme, 'scheduleEnabled' | 'availableFrom' | 'availableUntil'>, now = new Date()) {
  if (!theme.scheduleEnabled || !theme.availableFrom || !theme.availableUntil) return true;
  const from = new Date(theme.availableFrom);
  const until = new Date(theme.availableUntil);
  if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime())) return false;
  return now >= from && now <= until;
}

function emptyDraft(): AvailabilityDraft {
  return {
    liteCaptureEnabled: true,
    mobileUploadEnabled: false,
    scheduleEnabled: false,
    availableFrom: '',
    availableUntil: '',
    machineFilterEnabled: true,
    allowedMachineCodes: [],
  };
}

function draftFromTheme(theme: Theme): AvailabilityDraft {
  return {
    liteCaptureEnabled: theme.isActive !== false,
    mobileUploadEnabled: theme.mobileUploadProEnabled === true,
    scheduleEnabled: theme.scheduleEnabled === true,
    availableFrom: toLocalInput(theme.availableFrom),
    availableUntil: toLocalInput(theme.availableUntil),
    machineFilterEnabled: true,
    allowedMachineCodes: (theme.allowedMachineCodes || []).map((code) => code.toUpperCase()),
  };
}

const pinkFill = 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)';
const pinkHover = 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)';

const formDialogPaperSx = {
  m: { xs: 1.5, sm: 4 },
  width: { xs: 'calc(100% - 24px)', sm: '100%' },
  maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow: '0 24px 60px rgba(191, 95, 130, 0.18)',
  '&&': {
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    backgroundImage:
      'linear-gradient(165deg, #ffffff 0%, #fff8fb 72%, #fff5f8 100%), linear-gradient(135deg, #ffc1d4, #ff8ba7 55%, #f48fb1)',
    backgroundOrigin: 'padding-box, border-box',
    backgroundClip: 'padding-box, border-box',
  },
};

const formDialogTitleSx = {
  fontWeight: 800,
  color: '#bf5f82',
  background: 'linear-gradient(135deg, #fff5f8 0%, #ffeef3 100%)',
  borderBottom: '1px solid',
  borderColor: alpha('#ff8ba7', 0.35),
};

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#ff8ba7', 0.55) },
    '&:hover fieldset': { borderColor: '#ff8ba7' },
    '&.Mui-focused fieldset': { borderColor: '#f48fb1' },
  },
  '& .MuiInputLabel-root': { color: alpha('#bf5f82', 0.72) },
  '& .MuiInputLabel-root.Mui-focused': { color: '#bf5f82' },
};

const pinkSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundImage: 'linear-gradient(90deg, #ffc1d4, #ff8ba7)',
    opacity: 1,
  },
};

type ProfileDraft = {
  name: string;
  imageFile: File | null;
};

interface ThemeAvailabilityDialogProps {
  theme: Theme | null;
  open: boolean;
  mode?: 'settings' | 'create';
  channel?: ThemeChannel;
  apiCtx: ThemeServiceContext;
  userId?: string;
  onClose: () => void;
  onSaved: (theme: Theme) => void;
}

const ThemeAvailabilityDialog: React.FC<ThemeAvailabilityDialogProps> = ({
  theme,
  open,
  mode = 'settings',
  channel = 'pro',
  apiCtx,
  onClose,
  onSaved,
}) => {
  const { pages: t } = useI18n();
  const creating = mode === 'create';
  const [draft, setDraft] = useState<AvailabilityDraft | null>(null);
  const [profile, setProfile] = useState<ProfileDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const isPro = channel !== 'lite';

  useEffect(() => {
    if (!profile?.imageFile) {
      setImagePreview(theme?.themeImage?.url || '');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(profile.imageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profile?.imageFile, theme?.themeImage?.url]);

  useEffect(() => {
    if (!open) {
      if (creating) {
        setProfile(null);
        setDraft(null);
        setImagePreview('');
      }
      return;
    }
    if (creating) {
      setDraft(emptyDraft());
      setProfile({ name: '', imageFile: null });
      return;
    }
    if (!theme) return;
    setDraft(draftFromTheme(theme));
    setProfile({
      name: theme.name || '',
      imageFile: null,
    });
  }, [open, theme, isPro, creating]);

  const applyImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Chỉ được upload file ảnh');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Kích thước file không được vượt quá 10MB');
      return;
    }
    setProfile((prev) => (prev ? { ...prev, imageFile: file } : prev));
  };

  const handleSave = async () => {
    if (!profile || (!creating && !theme)) return;
    if (!profile.name.trim()) {
      toast.error('Tên theme là bắt buộc');
      return;
    }
    if (draft?.scheduleEnabled) {
      if (!draft.availableFrom || !draft.availableUntil) {
        toast.error('Cần chọn thời gian bắt đầu và kết thúc');
        return;
      }
      const from = new Date(draft.availableFrom);
      const until = new Date(draft.availableUntil);
      if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime()) || until <= from) {
        toast.error('Thời gian kết thúc phải sau thời gian bắt đầu');
        return;
      }
    }
    setSaving(true);
    try {
      if (creating) {
        const created = profile.imageFile
          ? await themeService.createThemeWithImage({
            name: profile.name.trim(),
            description: '',
            tags: [],
            themeImage: profile.imageFile,
            channel,
          }, apiCtx)
          : await themeService.createTheme({
            name: profile.name.trim(),
            description: '',
            tags: [],
            channel,
          }, apiCtx);
        let nextTheme = (created.theme || created) as Theme;
        if (draft && nextTheme?._id) {
          if (isPro && (nextTheme.isActive !== false) !== draft.liteCaptureEnabled) {
            const activeResponse = await themeService.setThemeActive(nextTheme._id, draft.liteCaptureEnabled, apiCtx);
            nextTheme = { ...nextTheme, ...(activeResponse.theme || {}), isActive: draft.liteCaptureEnabled };
          }
          if (isPro && (nextTheme.mobileUploadProEnabled === true) !== draft.mobileUploadEnabled) {
            const uploadResponse = await themeService.setMobileUploadProEnabled(nextTheme._id, draft.mobileUploadEnabled, apiCtx);
            nextTheme = { ...nextTheme, ...(uploadResponse.theme || {}), mobileUploadProEnabled: draft.mobileUploadEnabled };
          }
          const response = await themeService.setThemeAvailability(nextTheme._id, {
            scheduleEnabled: draft.scheduleEnabled,
            availableFrom: draft.availableFrom ? new Date(draft.availableFrom).toISOString() : null,
            availableUntil: draft.availableUntil ? new Date(draft.availableUntil).toISOString() : null,
            machineFilterEnabled: true,
            allowedMachineCodes: [],
          }, apiCtx);
          nextTheme = { ...nextTheme, ...(response.theme || {}) };
        }
        onSaved(nextTheme);
        toast.success('Tạo theme thành công');
        onClose();
        return;
      }

      if (!theme) return;
      const profileData = {
        name: profile.name.trim(),
        themeImage: profile.imageFile || undefined,
      };
      const profileResponse = profile.imageFile
        ? await themeService.updateThemeWithImage(theme._id, profileData, apiCtx)
        : await themeService.updateTheme(theme._id, profileData, apiCtx);
      const profileSaved = (profileResponse?.theme || profileResponse || {}) as Partial<Theme>;
      let nextTheme: Theme = {
        ...theme,
        ...profileSaved,
        name: profileData.name,
      };

      if (!draft) {
        onSaved(nextTheme);
        toast.success('Đã lưu cài đặt theme');
        onClose();
        return;
      }

      if (isPro && (theme.isActive !== false) !== draft.liteCaptureEnabled) {
        const activeResponse = await themeService.setThemeActive(theme._id, draft.liteCaptureEnabled, apiCtx);
        nextTheme = { ...nextTheme, ...(activeResponse.theme || {}), isActive: draft.liteCaptureEnabled };
      }

      if (isPro && (theme.mobileUploadProEnabled === true) !== draft.mobileUploadEnabled) {
        const uploadResponse = await themeService.setMobileUploadProEnabled(
          theme._id,
          draft.mobileUploadEnabled,
          apiCtx,
        );
        nextTheme = {
          ...nextTheme,
          ...(uploadResponse.theme || {}),
          mobileUploadProEnabled: draft.mobileUploadEnabled,
        };
      }

      const response = await themeService.setThemeAvailability(
        theme._id,
        {
          scheduleEnabled: draft.scheduleEnabled,
          availableFrom: draft.availableFrom ? new Date(draft.availableFrom).toISOString() : null,
          availableUntil: draft.availableUntil ? new Date(draft.availableUntil).toISOString() : null,
          machineFilterEnabled: true,
          allowedMachineCodes: draft.allowedMachineCodes,
        },
        apiCtx,
      );
      const saved = (response.theme || {}) as Partial<Theme>;
      onSaved({
        ...nextTheme,
        ...saved,
        isActive: draft.liteCaptureEnabled,
        mobileUploadProEnabled: draft.mobileUploadEnabled,
        scheduleEnabled: saved.scheduleEnabled === true,
        availableFrom: saved.availableFrom ?? null,
        availableUntil: saved.availableUntil ?? null,
        machineFilterEnabled: saved.machineFilterEnabled === true,
        allowedMachineCodes: saved.allowedMachineCodes || [],
      });
      toast.success('Đã lưu cài đặt theme');
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi lưu cài đặt theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: formDialogPaperSx }}
    >
      <DialogTitle sx={formDialogTitleSx}>{creating ? t.themes.dialogCreate : t.themes.scheduleTitle}</DialogTitle>
      <DialogContent sx={{ pt: '28px !important', px: { xs: 2, sm: 3 } }}>
        {profile && (
          <Box sx={{ display: 'grid', gap: 1.5, mb: draft ? 2.5 : 0, mt: 0.5 }}>
            <TextField
              label={t.themes.name}
              value={profile.name}
              onChange={(event) => setProfile((prev) => (prev ? { ...prev, name: event.target.value } : prev))}
              fullWidth
              required
              sx={formFieldSx}
            />
            <Paper
              elevation={0}
              component="label"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const file = event.dataTransfer.files?.[0];
                if (file) applyImageFile(file);
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                minHeight: 168,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 2.5,
                border: '1.5px dashed',
                borderColor: alpha('#ff8ba7', 0.7),
                bgcolor: '#fff',
                overflow: 'hidden',
                '&:hover': { borderColor: '#f48fb1', bgcolor: '#fff8fb' },
              }}
            >
              {imagePreview ? (
                <Box
                  component="img"
                  src={imagePreview}
                  alt=""
                  sx={{ width: '100%', height: 160, objectFit: 'contain', borderRadius: 2, bgcolor: '#fff0f5' }}
                />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 42, color: '#f48fb1' }} />
              )}
              <Typography variant="body2" fontWeight={800} color="#bf5f82">
                {t.themes.dropImage}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile.imageFile ? profile.imageFile.name : t.themes.imageFormats}
              </Typography>
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) applyImageFile(file);
                  event.target.value = '';
                }}
              />
              {profile.imageFile ? (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setProfile((prev) => (prev ? { ...prev, imageFile: null } : prev));
                  }}
                  sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#fff', color: '#bf5f82' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Paper>
          </Box>
        )}

        {draft && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t.themes.scheduleHint}
            </Typography>
            {isPro && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 3,
                rowGap: 0.5,
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.liteCaptureEnabled}
                    onChange={(_, checked) =>
                      setDraft((prev) => (prev ? { ...prev, liteCaptureEnabled: checked } : prev))
                    }
                    color="primary"
                    sx={pinkSwitchSx}
                  />
                }
                label={
                  <Typography fontWeight={800} lineHeight={1}>
                    {t.themes.activeMliteLabel}
                  </Typography>
                }
                sx={{ m: 0, alignItems: 'center' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.mobileUploadEnabled}
                    onChange={(_, checked) =>
                      setDraft((prev) => (prev ? { ...prev, mobileUploadEnabled: checked } : prev))
                    }
                    color="primary"
                    sx={pinkSwitchSx}
                  />
                }
                label={
                  <Typography fontWeight={800} lineHeight={1}>
                    {t.themes.mobileUploadProTitle}
                  </Typography>
                }
              sx={{ m: 0, alignItems: 'center' }}
            />
            </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={draft.scheduleEnabled}
                  onChange={(_, checked) => setDraft((prev) => (prev ? { ...prev, scheduleEnabled: checked } : prev))}
                  color="primary"
                  sx={pinkSwitchSx}
                />
              }
              label={
                <Typography fontWeight={800} lineHeight={1}>
                  {t.themes.scheduleEnable}
                </Typography>
              }
              sx={{ m: 0, alignItems: 'center' }}
            />
            <Collapse in={draft.scheduleEnabled} unmountOnExit>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                <TextField
                  label={t.themes.scheduleFrom}
                  type="datetime-local"
                  value={draft.availableFrom}
                  onChange={(event) =>
                    setDraft((prev) => (prev ? { ...prev, availableFrom: event.target.value } : prev))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={formFieldSx}
                />
                <TextField
                  label={t.themes.scheduleUntil}
                  type="datetime-local"
                  value={draft.availableUntil}
                  onChange={(event) =>
                    setDraft((prev) => (prev ? { ...prev, availableUntil: event.target.value } : prev))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={formFieldSx}
                />
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          gap: 1,
          borderTop: '1px solid',
          borderColor: alpha('#ff8ba7', 0.28),
          background: 'linear-gradient(180deg, #ffffff 0%, #fff8fb 100%)',
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            color: '#bf5f82',
          }}
        >
          {t.common.cancel}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !profile}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 999,
            color: '#fff',
            background: pinkFill,
            boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
            '&:hover': { background: pinkHover },
            '&.Mui-disabled': { color: '#fff', background: alpha('#ff8ba7', 0.45), boxShadow: 'none' },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : (creating ? t.common.create : t.common.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemeAvailabilityDialog;
